/**
 * Thèmes sonores pour le timer de récupération (bips de décompte 5-4-3-2-1 + "GO").
 * Web Audio API — aucune dépendance externe.
 */
const ctxRef = { current: null };
const getCtx = () => {
  // Le contexte peut passer en état "closed" (pas juste "suspended") si le navigateur le
  // ferme agressivement (écran verrouillé / app en arrière-plan pendant le repos, fréquent
  // sur mobile) — un contexte fermé ne peut JAMAIS être relancé via resume(), il faut en
  // recréer un neuf, sinon le son reste muet pour le reste de la séance (bug signalé).
  if (!ctxRef.current || ctxRef.current.state === 'closed') {
    try { ctxRef.current = new (window.AudioContext || window.webkitAudioContext)(); } catch { return null; }
  }
  return ctxRef.current;
};

const _playTone = (ctx, freq, duration, type, gainVal) => {
  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.type = type;
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(gainVal, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    osc.start();
    osc.stop(ctx.currentTime + duration);
  } catch { /* audio indisponible */ }
};

const tone = (freq, duration, type = 'sine', gainVal = 0.16) => {
  const ctx = getCtx();
  if (!ctx) return;
  // Le navigateur suspend parfois le contexte audio (économie de batterie, onglet en arrière-plan...).
  // Sans reprise explicite, TOUS les sons restent silencieux pour le reste de la session — on
  // relance systématiquement avant chaque son plutôt qu'une seule fois à la création.
  if (ctx.state === 'suspended') {
    ctx.resume().then(() => _playTone(ctx, freq, duration, type, gainVal)).catch(() => {});
    return;
  }
  _playTone(ctx, freq, duration, type, gainVal);
};

/** Débloque l'audio dès le premier geste utilisateur (clic "Commencer"/"Valider la série") —
 * garantit que le contexte est bien "running" avant que le décompte silencieux (déclenché par
 * un timer, pas un clic direct) n'essaie de jouer un son. */
export function unlockAudio() {
  const ctx = getCtx();
  if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
}

export const SOUND_THEMES = [
  { value: 'classique', label: 'Classique' },
  { value: '8bit', label: '8-bit' },
  { value: 'boxe', label: 'Cloche de boxe' },
  { value: 'digital', label: 'Digital' },
  { value: 'arcade', label: 'Arcade' },
  { value: 'zen', label: 'Zen' },
  { value: 'tambour', label: 'Tambour' },
  { value: 'silencieux', label: 'Silencieux' },
];

/** Léger ajustement de hauteur/rythme selon le type d'exercice à venir : cardio = plus aigu et
 * plus vif (énergique), force = inchangé (grave, impact solide). Appliqué sur tous les thèmes. */
const toneC = (freq, duration, type, gainVal, isCardio) => {
  tone(isCardio ? freq * 1.3 : freq, isCardio ? duration * 0.75 : duration, type, gainVal);
};

export function playCountdownTick(theme, secondsLeft, isCardio = false) {
  switch (theme) {
    case '8bit':
      toneC(secondsLeft <= 3 ? 880 : 660, 0.08, 'square', 0.08, isCardio);
      break;
    case 'boxe':
      toneC(160, 0.15, 'sawtooth', 0.18, isCardio);
      break;
    case 'digital':
      toneC(secondsLeft <= 3 ? 1400 : 1000, 0.06, 'square', 0.07, isCardio);
      break;
    case 'arcade':
      toneC(secondsLeft <= 3 ? 950 : 700, 0.05, 'square', 0.09, isCardio);
      setTimeout(() => toneC(secondsLeft <= 3 ? 1200 : 900, 0.05, 'square', 0.07, isCardio), 60);
      break;
    case 'zen':
      toneC(392, 0.5, 'triangle', 0.12, isCardio);
      break;
    case 'tambour':
      toneC(90, 0.18, 'triangle', 0.2, isCardio);
      break;
    case 'silencieux':
      break;
    default:
      toneC(700, 0.12, 'sine', 0.14, isCardio);
  }
}

export function playGo(theme, isCardio = false) {
  switch (theme) {
    case '8bit':
      toneC(660, 0.09, 'square', 0.12, isCardio); setTimeout(() => toneC(990, 0.18, 'square', 0.12, isCardio), 100);
      break;
    case 'boxe':
      toneC(440, 0.5, 'triangle', 0.2, isCardio); setTimeout(() => toneC(440, 0.4, 'triangle', 0.18, isCardio), 250);
      break;
    case 'digital':
      toneC(1000, 0.08, 'square', 0.1, isCardio); setTimeout(() => toneC(1600, 0.22, 'square', 0.1, isCardio), 90);
      break;
    case 'arcade':
      toneC(600, 0.07, 'square', 0.1, isCardio);
      setTimeout(() => toneC(900, 0.07, 'square', 0.1, isCardio), 80);
      setTimeout(() => toneC(1300, 0.25, 'square', 0.11, isCardio), 160);
      break;
    case 'zen':
      toneC(523, 0.7, 'triangle', 0.16, isCardio); setTimeout(() => toneC(659, 0.9, 'triangle', 0.14, isCardio), 150);
      break;
    case 'tambour':
      toneC(110, 0.3, 'triangle', 0.22, isCardio); setTimeout(() => toneC(140, 0.35, 'triangle', 0.2, isCardio), 140);
      break;
    case 'silencieux':
      break;
    default:
      toneC(1200, 0.35, 'sine', 0.18, isCardio);
  }
}

/** Petit son de victoire (arpège ascendant) — joué en complément de la voix quand un nouveau
 * record personnel AMRAP est battu (Cardio) ou objectif de tonnage de séance atteint (Musculation). */
export function playVictoryFanfare() {
  const notes = [523.25, 659.25, 783.99, 1046.5];
  notes.forEach((freq, i) => setTimeout(() => tone(freq, 0.35, 'triangle', 0.18), i * 120));
}
